import { useCallback, useEffect, useState } from 'react';

export interface Position {
  latitude: number;
  longitude: number;
}

/**
 * Where the phone is now, asked of the browser once, for a screen that
 * writes down where something was done: an inspection's run.
 *
 * Not useDriverStateV2's tracking, which polls the driver's calls and
 * reports the position to the server; this only answers the screen. It asks
 * on open only where the browser has already been given the permission, so
 * a first-time prompt waits for a tap and arrives attached to something the
 * member did. Inside a wrapper the web view's own geolocation answers; the
 * bridge has no message to ask the phone for a fix (docs/native-bridge.md).
 */
export function useCurrentPosition() {
  const [position, setPosition] = useState<Position | null>(null);
  const [locating, setLocating] = useState(false);
  const [failed, setFailed] = useState(false);

  const locate = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setFailed(true);
      return;
    }
    setLocating(true);
    setFailed(false);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setPosition({ latitude: coords.latitude, longitude: coords.longitude });
        setLocating(false);
      },
      () => {
        setFailed(true);
        setLocating(false);
      },
      // A fix up to five minutes old is where the member still is.
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 300_000 }
    );
  }, []);

  useEffect(() => {
    let dropped = false;
    navigator.permissions
      ?.query({ name: 'geolocation' })
      .then((permission) => {
        if (!dropped && permission.state === 'granted') locate();
      })
      .catch(() => {
        // Safari before 16 has no permissions API. The tap still works.
      });
    return () => {
      dropped = true;
    };
  }, [locate]);

  return { position, locating, failed, locate };
}
